/* Hallmark · component: SegmentedControl · genre: modern-minimal · theme: maeiz-tokens
 * Generic fieldset-style segmented pills — any label, any options (UI only).
 * Outer height matches Input/Select (h-11); inner pills are shorter.
 * motion: none on selection
 */
import React from "react";
import { clsx } from "clsx";
import type { LucideIcon } from "lucide-react";
import { LayoutGrid, List, Table2 } from "lucide-react";
import { CONTROL_HEIGHT } from "../../styles/field";

export interface SegmentedOption<T extends string = string> {
    value: T;
    label: string;
    /** Lucide icon component or custom node */
    icon?: LucideIcon | React.ReactNode;
    disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
    options: SegmentedOption<T>[];
    value: T;
    onChange: (value: T) => void;
    /** Floating legend on the top border (e.g. "View as", "Filtered by") */
    label?: string;
    className?: string;
    fullWidth?: boolean;
    /** Outer shell height — matches Input/Select md by default */
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    id?: string;
}

function renderOptionIcon(icon: LucideIcon | React.ReactNode | undefined, _active?: boolean) {
    if (icon == null) return null;
    if (React.isValidElement(icon)) return icon;
    if (typeof icon === "function" || typeof icon === "object") {
        const Comp = icon as LucideIcon;
        return <Comp size={15} strokeWidth={2} aria-hidden className="shrink-0" />;
    }
    return null;
}

export function SegmentedControl<T extends string = string>({
    options,
    value,
    onChange,
    label,
    className,
    fullWidth = false,
    size = "md",
    disabled = false,
    id,
}: SegmentedControlProps<T>) {
    const groupLabel = label ?? "Options";

    return (
        <fieldset
            id={id}
            disabled={disabled}
            className={clsx(
                "relative inline-flex max-w-full items-center rounded-2xl border border-neutral-dark/80 bg-white dark:border-white/40 dark:bg-neutral-navy",
                CONTROL_HEIGHT[size],
                "px-1",
                fullWidth && "w-full",
                disabled && "opacity-50",
                className,
            )}
        >
            {label ? (
                <legend className="absolute -top-2.5 start-3 px-0.5 leading-none">
                    <span className="inline-block rounded-full border border-neutral-dark/70 bg-white px-2 py-0.5 text-[11px] font-medium tracking-wide text-primary dark:border-gold/50 dark:bg-neutral-navy dark:text-gold-light">
                        {label}
                    </span>
                </legend>
            ) : null}
            <div
                className={clsx("flex h-full min-w-0 flex-1 items-center gap-0.5", fullWidth && "w-full")}
                role="radiogroup"
                aria-label={groupLabel}
            >
                {options.map((opt) => {
                    const active = value === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            disabled={disabled || opt.disabled}
                            onClick={() => onChange(opt.value)}
                            className={clsx(
                                // Shorter pills inside the h-11 shell
                                "inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-medium",
                                "disabled:pointer-events-none disabled:opacity-40",
                                fullWidth && "min-w-0 flex-1",
                                active
                                    ? "bg-primary font-semibold text-white"
                                    : "text-neutral-secondary hover:bg-neutral-light/80 hover:text-neutral-dark dark:hover:bg-white/10 dark:hover:text-white",
                            )}
                        >
                            {renderOptionIcon(opt.icon, active)}
                            <span className="truncate">{opt.label}</span>
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
}

/** Preset options for data layout switching — optional convenience only */
export const VIEW_AS_OPTIONS: SegmentedOption<"grid" | "list" | "table">[] = [
    { value: "grid", label: "Grid", icon: LayoutGrid },
    { value: "list", label: "List", icon: List },
    { value: "table", label: "Table", icon: Table2 },
];

export type ViewAsMode = "grid" | "list" | "table";

/** Thin preset: SegmentedControl + View as / Grid·List·Table defaults. Prefer SegmentedControl for custom sets. */
export interface ViewAsProps {
    value?: ViewAsMode;
    onChange?: (mode: ViewAsMode) => void;
    options?: SegmentedOption<ViewAsMode>[];
    label?: string;
    className?: string;
    fullWidth?: boolean;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    /** @deprecated use value */
    viewMode?: ViewAsMode;
    /** @deprecated use onChange */
    onViewModeChange?: (mode: ViewAsMode) => void;
}

export const ViewAs: React.FC<ViewAsProps> = ({
    value,
    onChange,
    options = VIEW_AS_OPTIONS,
    label = "View as",
    className,
    fullWidth,
    size,
    disabled,
    viewMode,
    onViewModeChange,
}) => {
    const current = value ?? viewMode ?? "table";
    const set = onChange ?? onViewModeChange ?? (() => undefined);
    return (
        <SegmentedControl
            options={options}
            value={current}
            onChange={set}
            label={label}
            className={className}
            fullWidth={fullWidth}
            size={size}
            disabled={disabled}
        />
    );
};

/** @deprecated Prefer SegmentedControl or ViewAs */
export type ViewMode = ViewAsMode;
/** @deprecated Prefer SegmentedControl or ViewAs */
export const ViewToggle = ViewAs;
export type ViewToggleProps = ViewAsProps;
